import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface AuthUser {
  id: string;
  email: string;
  role?: string;
  tenantId?: string;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'gscg.auth.v1';

interface StoredAuth {
  token: string;
  refreshToken?: string;
  expiresAt: number;
  user: AuthUser;
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuth;
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ user: null, token: null, loading: true });

  useEffect(() => {
    const stored = loadStored();
    if (stored) {
      setState({ user: stored.user, token: stored.token, loading: false });
    } else {
      setState({ user: null, token: null, loading: false });
    }
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(body.message ?? `Login failed (HTTP ${res.status})`);
    }
    const data = (await res.json()) as {
      accessToken: string;
      refreshToken?: string;
      expiresIn?: number;
      user: { id: string; email: string };
    };
    const user: AuthUser = { id: data.user.id, email: data.user.email };
    const expiresAt = Date.now() + (data.expiresIn ?? 3600) * 1000;
    const stored: StoredAuth = {
      token: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt,
      user,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    setState({ user, token: data.accessToken, loading: false });
  };

  const logout = (): void => {
    localStorage.removeItem(STORAGE_KEY);
    setState({ user: null, token: null, loading: false });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export async function authFetch(
  token: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  return fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });
}
